/**
 * Multi-tenant configuration for different domains
 * Each tenant can have its own branding, language settings, and backend API
 */

export const tenantConfigs = {
  // Production: domain1.com (English primary)
  'domain1.com': {
    name: 'PremiumStore',
    apiUrl: 'https://api.domain1.com',
    defaultLanguage: 'en',
    supportedLanguages: ['en', 'cs'],
    theme: {
      primary: '#3B82F6',    // blue-600
      secondary: '#1E40AF',  // blue-800
    },
    seo: {
      defaultTitle: 'PremiumStore - Compare Prices & Find Best Deals',
      defaultDescription: 'Your premium multi-vendor marketplace. Compare prices and find the best deals from trusted vendors.',
    }
  },

  // Production: domain2.cz (Czech only)
  'domain2.cz': {
    name: 'ObchodCZ',
    apiUrl: 'https://api.domain2.cz',
    defaultLanguage: 'cs',
    supportedLanguages: ['cs'],
    theme: {
      primary: '#059669',    // emerald-600
      secondary: '#047857',  // emerald-700
    },
    seo: {
      defaultTitle: 'ObchodCZ - Porovnejte ceny a najděte nejlepší nabídky',
      defaultDescription: 'Vaše prémiové multi-vendor tržiště. Porovnejte ceny a najděte nejlepší nabídky od důvěryhodných prodejců.',
    }
  },

  // Production: domain3.sk (Slovak primary)
  'domain3.sk': {
    name: 'ObchodSK',
    apiUrl: 'https://api.domain3.sk',
    defaultLanguage: 'sk',
    supportedLanguages: ['sk', 'en'],
    theme: {
      primary: '#DC2626',    // red-600
      secondary: '#B91C1C',  // red-700
    },
    seo: {
      defaultTitle: 'ObchodSK - Porovnajte ceny a nájdite najlepšie ponuky',
      defaultDescription: 'Vaše prémiové multi-vendor tržisko. Porovnajte ceny a nájdite najlepšie ponuky od dôveryhodných predajcov.',
    }
  },

  // Production: hello.2d2.cz (Czech primary)
  'hello.2d2.cz': {
    name: 'Hello2D2',
    apiUrl: 'https://vendor.2d2.cz',
    defaultLanguage: 'cs',
    supportedLanguages: ['cs', 'en'],
    theme: {
      primary: '#8B5CF6',    // violet-600
      secondary: '#7C3AED',  // violet-700
    },
    seo: {
      defaultTitle: 'Hello2D2 - Porovnejte ceny a najděte nejlepší nabídky',
      defaultDescription: 'Vaše prémiové multi-vendor tržiště. Porovnejte ceny a najděte nejlepší nabídky od důvěryhodných prodejců.',
    }
  },

  // Local development
  'localhost': {
    name: 'DevStore',
    apiUrl: 'http://localhost:3006',
    defaultLanguage: 'cs',
    supportedLanguages: ['cs', 'en'],
    theme: {
      primary: '#3B82F6',
      secondary: '#1E40AF',
    },
    seo: {
      defaultTitle: 'DevStore - Development',
      defaultDescription: 'Development environment for multi-vendor marketplace.',
    }
  },

  // Cloudflare Pages preview URLs (*.pages.dev)
  '*.pages.dev': {
    name: 'Preview',
    apiUrl: 'https://api-staging.domain1.com',
    defaultLanguage: 'en',
    supportedLanguages: ['en', 'cs'],
    theme: {
      primary: '#3B82F6',
      secondary: '#1E40AF',
    },
    seo: {
      defaultTitle: 'Preview - MultiVendor Marketplace',
      defaultDescription: 'Preview environment for multi-vendor marketplace.',
    }
  }
};

/**
 * Get tenant configuration based on current hostname
 * @returns {Object} Tenant configuration
 */
export const getCurrentTenant = () => {
  const hostname = window.location.hostname;

  // Exact match
  if (tenantConfigs[hostname]) {
    return tenantConfigs[hostname];
  }

  // Wildcard match for *.pages.dev (Cloudflare Pages preview URLs)
  if (hostname.endsWith('.pages.dev')) {
    return tenantConfigs['*.pages.dev'];
  }

  // Local development variations (127.0.0.1, localhost with port)
  if (hostname.includes('localhost') || hostname === '127.0.0.1') {
    return tenantConfigs['localhost'];
  }

  // Fallback to domain1.com config
  console.warn(`No tenant config found for hostname: ${hostname}. Using domain1.com as fallback.`);
  return tenantConfigs['domain1.com'];
};

/**
 * Get API base URL for current tenant
 * @returns {string} API base URL
 */
export const getApiUrl = () => {
  return getCurrentTenant().apiUrl;
};

/**
 * Get supported languages for current tenant
 * @returns {Array<string>} Array of language codes (e.g., ['en', 'cs'])
 */
export const getSupportedLanguages = () => {
  return getCurrentTenant().supportedLanguages;
};

/**
 * Get default language for current tenant
 * @returns {string} Language code (e.g., 'en' or 'cs')
 */
export const getDefaultLanguage = () => {
  return getCurrentTenant().defaultLanguage;
};

/**
 * Get theme colors for current tenant
 * @returns {Object} Theme configuration
 */
export const getTheme = () => {
  return getCurrentTenant().theme;
};

/**
 * Check if a language is supported by current tenant
 * @param {string} lang - Language code to check
 * @returns {boolean}
 */
export const isLanguageSupported = (lang) => {
  return getSupportedLanguages().includes(lang);
};
